import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4 font-heading">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
          <Link to="/category/wenjian-qicao" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const jsonLdArticle = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.date,
    "url": `https://17.c/article/${article.slug}`,
    "author": { "@type": "Organization", "name": "17.c 起草官网" },
    "publisher": { "@type": "Organization", "name": "17.c 起草官网", "url": "https://17.c" },
    "isPartOf": { "@type": "WebSite", "name": "17.c 起草官网", "url": "https://17.c" }
  });

  const jsonLdBreadcrumb = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://17.c" },
      { "@type": "ListItem", "position": 2, "name": article.category, "item": `https://17.c/category/${article.categorySlug}` },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://17.c/article/${article.slug}` }
    ]
  });

  return (
    <>
      <title>{article.title} - 17.c 起草官网</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`17.c 起草官网, ${article.category}, 文件起草服务, 官网建设, 专业起草平台`} />
      <link rel="canonical" href={`https://17.c/article/${article.slug}`} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://17.c/article/${article.slug}`} />
      <script type="application/ld+json">{jsonLdArticle}</script>
      <script type="application/ld+json">{jsonLdBreadcrumb}</script>

      <div className="min-h-screen bg-background-50">
        <div className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <Breadcrumb
              items={[
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-400">{article.date}</span>
                <span className="text-sm text-foreground-400">阅读时间约 {article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight font-heading">
                {article.title}
              </h1>
            </header>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-700 leading-relaxed
                prose-headings:text-foreground-900 prose-headings:font-heading prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:my-4 prose-li:text-foreground-700 prose-li:mb-1
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回 {article.category}
                </Link>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14 md:py-20">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 font-heading">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.listImageUrl}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-2">
                          {ra.category}
                        </span>
                        <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}